import React from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import {
    getHeapUserId,
    BusinessUnits,
    navigateWindowTo,
    ProductCardBrief,
    LegacyCityPropType,
    getGiftOfferSummary,
    PRODUCT_CARD_VARIANTS,
} from '@ratehub/base-ui';
import {
    CreditCardCTA,
    getCardBannerMessage,
    CreditCardShape,
    useApplyForCreditCardCallback,
} from '@ratehub/cc-common';
import { RATEHUB_BROKERAGE_IDS, RATEHUB_EXCLUSIVE_IDS } from '@ratehub/mtg-common';
import { LoanShape, useApplyForLoanCallback } from '@ratehub/loans-loanfinder';

import { Products } from '../definitions/Products';


function FeaturedProduct({
    className,

    isSponsored,
    hasPromo,

    cardTitle,
    applyText,
    applyHref,
    applyHrefRedirect,
    imageSrc,
    imageAlt,
    rate,
    description,
    productTypeOverride,

    productType,
    providerId,
    providerName,

    product,            // required by CC and Loans
    analyticsObject,

    mortgageCallback,

    hasMinWidth = true,
    hasMaxWidth = true,
    isCompact = false,
    variant = PRODUCT_CARD_VARIANTS.ONE_TITLE,

    as = 'li',

    city,
    hideCTAIfNotMonetized = false,
    hideCheckEligibility = false,
    ...otherProps
}) {
    const intl = useIntl();
    const applyForCreditCardCallback = useApplyForCreditCardCallback();
    const applyForLoanCallback = useApplyForLoanCallback();

    const isARatehubCompany = productType === Products.MORTGAGES
        && Object.values(RATEHUB_BROKERAGE_IDS).includes(providerId);

    const isRatehubExclusive = productType === Products.MORTGAGES
        && Object.values(RATEHUB_EXCLUSIVE_IDS).includes(providerId);

    /* Experimental prop - currently a CC-only experiment, but if it
    wins, this will need to come from someplace else for the other BUs
    I would suggest setting a productTitle as part of each product
    during enrichment but this is a quick way to do this for CC
    without having to re-work that whole system. Discussing with
    product where this would come from in other BUs now. */
    const productName = product?.name;

    async function handleCCCallback() {
        applyForCreditCardCallback({
            cardInfo: product,
            applyURLOverride: applyHrefRedirect ?? applyHref,
            componentName: 'FeaturedProduct',
        });
    }

    async function handleLoanCallback() {
        applyForLoanCallback({
            loan: product,
            applyURLOverride: applyHrefRedirect ?? applyHref,
            componentName: 'FeaturedProduct',
        });
    }

    async function handleMortgageClick() {
        mortgageCallback?.({ rate, city });
    }

    function getComponentWithProps() {
        const componentClassName = hasPromo
            ? 'has-promo'
            : '';

        switch (productType) {
            case Products.CREDIT_CARDS: {
                const {
                    isMonetized,
                    cardTypes,
                    giftCardOffer,
                    hasReferralOffer,
                    referralOfferValue,
                    prohibitedProvinces,
                } = product;

                return hideCTAIfNotMonetized && !isMonetized
                    ? {
                        CTAComponent: 'div',
                        propsForCTAComponent: {},
                    }
                    : {
                        CTAComponent: CreditCardCTA,
                        propsForCTAComponent: {
                            cardId: product.id,
                            hasEligibilityCTA: !hideCheckEligibility,
                            giftCardOffer,
                            hasReferralOffer,
                            isMonetized,
                            prohibitedProvinces,

                            cardTypes,
                            onClick: handleCCCallback,
                            buttonSize: 'small',
                            className: componentClassName,
                            productType: productTypeOverride,
                            applyText: applyText?.trim().length > 0
                                ? applyText
                                : undefined,
                        },
                        cardBannerMessage: getCardBannerMessage({
                            giftCardOffer,
                            hasReferralOffer,
                            referralOfferValue,
                        }, intl),
                    };
            }

            case Products.LOANS: {
                return {
                    CTAComponent: 'div',
                    propsForCTAComponent: {
                        onClick: handleLoanCallback,
                        className: componentClassName,
                    },
                    ...product?.giftOffer && {
                        cardBannerMessage: getGiftOfferSummary({ giftCardOffer: product.giftOffer, intl }),
                    },
                };
            }

            case Products.MORTGAGES:
                return {
                    CTAComponent: 'div',
                    propsForCTAComponent: {
                        onClick: handleMortgageClick,
                        className: componentClassName,
                    },
                };

            default:
                return {
                    CTAComponent: 'div',
                    propsForCTAComponent: {
                        onClick: () => navigateWindowTo(applyHrefRedirect || applyHref, {
                            isNewTab: true,
                            queryParams: {
                                component_name: 'FeaturedProduct',
                                heap_id: getHeapUserId(),
                            },
                        }),
                        className: componentClassName,
                    },
                };
        }
    }

    return (
        <ProductCardBrief
            {...otherProps}
            analyticsObject={analyticsObject}
            className={className}
            CTAComponentWithProps={getComponentWithProps()}
            isSponsored={isSponsored}
            rate={rate}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
            isARatehubCompany={isARatehubCompany}
            isRatehubExclusive={isRatehubExclusive}
            description={description}
            productType={productType}
            applyText={applyText}
            cardTitle={cardTitle}
            productName={productName}
            providerName={providerName}
            hasPromo={hasPromo}
            hasMinWidth={hasMinWidth}
            hasMaxWidth={hasMaxWidth}
            isCompact={isCompact}
            variant={variant}
            as={as}
        />
    );
}

FeaturedProduct.propTypes = {
    className: PropTypes.string,

    isSponsored: PropTypes.bool.isRequired,
    hasPromo: PropTypes.bool.isRequired,

    cardTitle: PropTypes.string.isRequired,

    applyHref: PropTypes.string.isRequired,
    applyText: PropTypes.string,
    imageSrc: PropTypes.string.isRequired,
    imageAlt: PropTypes.string.isRequired,

    applyHrefRedirect: PropTypes.string,
    rate: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.object,
    ]),
    description: PropTypes.string,
    productTypeOverride: PropTypes.string,

    productType: PropTypes.oneOf(
        [ ...new Set(Object.values(Products).concat(Object.values(BusinessUnits))) ],
    ).isRequired,

    providerId: PropTypes.number,
    providerName: PropTypes.string,

    product: PropTypes.oneOfType([
        PropTypes.shape(CreditCardShape),
        PropTypes.shape(LoanShape),
    ]),
    analyticsObject: PropTypes.object.isRequired,

    mortgageCallback: PropTypes.func,

    hasMinWidth: PropTypes.bool,
    hasMaxWidth: PropTypes.bool,
    isCompact: PropTypes.bool,
    variant: PropTypes.oneOf(Object.values(PRODUCT_CARD_VARIANTS)),

    as: PropTypes.string,
    city: LegacyCityPropType.isRequired,
    hideCTAIfNotMonetized: PropTypes.bool,      // this prop is only relevant to credit cards
    hideCheckEligibility: PropTypes.bool,       // this prop is only relevant to credit cards
};

export default FeaturedProduct;
